import { OutlineNodeType, PaperOutlineNode } from "../types/Writer/OutlineNode";
import { SelectContent } from "../types/Writer/SelectContents";

/**
 * Get an outline node by its id
 * @param nodeId The id of the node to find
 * @param nodes The list of nodes to search
 */
export function getOutlineNodeById(nodeId: number, nodes: PaperOutlineNode[] | Omit<PaperOutlineNode, "children">[]): PaperOutlineNode | Omit<PaperOutlineNode, "children"> | undefined {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }

    if ("children" in node) {
      const result = getOutlineNodeById(nodeId, node.children);
      if (result !== undefined) {
        return result;
      }
    }
  }

  return undefined;
}

export const getParagraphContent = (node: PaperOutlineNode): PaperOutlineNode[] => {
  let paragraphs: PaperOutlineNode[] = [];

  // If the node is a paragraph, add its content
  if (node.type === OutlineNodeType.Paragraph && node.content) {
    paragraphs.push(node);
  }

  // If the node has children, recursively check them
  if (node.children && node.children.length > 0) {
    node.children.forEach(child => {
      paragraphs = paragraphs.concat(getParagraphContent(child));
    });
  }

  return paragraphs;
};

/**
 * Flatten sectionNode to get all nodes with content
 * @param node The section node to flatten
 * @returns A flat array of all nodes with their content
 */
export const flattenSectionNode = (node: PaperOutlineNode): PaperOutlineNode[] => {
  const result: PaperOutlineNode[] = [];

  const traverse = (n: PaperOutlineNode) => {
    // Add node if it has content (paragraph, section name, etc.)
    if (n.content || n.name) {
      result.push(n);
    }

    // Recursively process children
    if (n.children && n.children.length > 0) {
      n.children.forEach(child => traverse(child));
    }
  };

  traverse(node);
  return result;
};

/**
 * Strip HTML tags from content for comparison
 * @param content The content string that may contain HTML
 * @returns Plain text without HTML tags
 */
const stripHtmlTags = (content: string): string => {
  if (!content) return '';
  return content.replace(/<[^>]*>/g, '').trim();
};

/**
 * Find matching nodes from sectionNode based on selected content from Tiptap
 * This ensures IDs are consistent between sectionNode and selectContents
 *
 * @param sectionNode The current section node from outline store
 * @param tiptapSelectedNodes The nodes converted from Tiptap selection (with potentially wrong IDs)
 * @returns SelectContent array with correct IDs from sectionNode
 */
export const findMatchingNodesFromSection = (
  sectionNode: PaperOutlineNode,
  tiptapSelectedNodes: any[]
): SelectContent[] => {
  const result: SelectContent[] = [];

  // Flatten the section node to get all nodes
  const flatNodes = flattenSectionNode(sectionNode);

  // Build a map for quick lookup by content
  const contentToNodeMap = new Map<string, PaperOutlineNode>();
  flatNodes.forEach(node => {
    const content = node.content || node.name || '';
    const strippedContent = stripHtmlTags(content);
    if (strippedContent) {
      contentToNodeMap.set(strippedContent, node);
    }
  });

  // Process each selected node from Tiptap
  const processNode = (tiptapNode: any) => {
    const selectContent = tiptapNode.select_content || tiptapNode.content || '';
    const strippedSelectContent = stripHtmlTags(selectContent);

    if (strippedSelectContent) {
      // Try to find matching node in sectionNode by content
      const matchedNode = contentToNodeMap.get(strippedSelectContent);

      if (matchedNode) {
        result.push({
          id: matchedNode.id as number,
          select_content: selectContent,
          type: matchedNode.type as OutlineNodeType,
          refer: matchedNode.refer,
          equation: matchedNode.equation,
          cite: matchedNode.cite,
        });
      }
    }

    // Process children if any (for nested lists)
    if (tiptapNode.children && tiptapNode.children.length > 0) {
      tiptapNode.children.forEach((child: any) => processNode(child));
    }
  };

  // Process all tiptap selected nodes
  tiptapSelectedNodes.forEach(node => processNode(node));

  return result;
};

/**
 * Alternative approach: Find nodes by matching content with the full section tree
 * This handles partial text selection within a node
 *
 * @param sectionNode The current section node
 * @param selectedText The plain text that user selected
 * @returns SelectContent array with correct IDs
 */
export const findNodesBySelectedText = (
  sectionNode: PaperOutlineNode,
  selectedText: string
): SelectContent[] => {
  const result: SelectContent[] = [];
  const normalizedSelectedText = selectedText.trim();

  if (!normalizedSelectedText) return result;

  const traverse = (node: PaperOutlineNode) => {
    const nodeContent = node.content || node.name || '';
    const strippedContent = stripHtmlTags(nodeContent);

    // Check if this node's content is part of the selection or contains the selection
    if (strippedContent && (
      normalizedSelectedText.includes(strippedContent) ||
      strippedContent.includes(normalizedSelectedText)
    )) {
      // Determine the actual selected content
      const actualSelectContent = strippedContent.includes(normalizedSelectedText)
        ? normalizedSelectedText
        : strippedContent;

      result.push({
        id: node.id as number,
        select_content: actualSelectContent,
        type: node.type as OutlineNodeType,
        refer: node.refer,
        equation: node.equation,
        cite: node.cite,
      });
    }

    // Recursively check children
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => traverse(child));
    }
  };

  traverse(sectionNode);
  return result;
};